/**
 * KeyDisplay: HUD widget showing the player's collected keys.
 *
 * Displays up to 3 key icons in a row. Each key is shown as a small
 * sprite matching its tier color. When a key is used, it plays a
 * magical disappearing effect (sparkle + shrink + fade).
 */

import Phaser from 'phaser';
import {
  KEY_TEXTURE_MAP,
  KeyTier,
  TEX_GLOW,
} from '../utils/constants.js';
import {
  COLOR_METALLIC,
  COLOR_COPPER,
  COLOR_GOLD,
} from '../config/game-config.js';

/** @type {Object<string, number>} */
const TIER_COLOR = Object.freeze({
  [KeyTier.METALLIC]: COLOR_METALLIC,
  [KeyTier.COPPER]: COLOR_COPPER,
  [KeyTier.GOLD]: COLOR_GOLD,
});

export default class KeyDisplay {
  /**
   * @param {Phaser.Scene} scene - The HUD scene
   * @param {number} x - Left edge X position
   * @param {number} y - Center Y position
   */
  constructor(scene, x, y) {
    this.scene = scene;
    this.x = x;
    this.y = y;

    /** @type {string[]} Array of tier strings for keys the player holds. */
    this._keys = [];

    /** @type {Phaser.GameObjects.Sprite[]} Key icon sprites. */
    this._sprites = [];

    // Label
    this._label = scene.add.text(x, y, 'KEYS:', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '10px',
      color: '#888888',
    }).setOrigin(0, 0.5).setDepth(210);
  }

  /**
   * Set the full list of keys the player currently holds.
   * Rebuilds all icons.
   *
   * @param {string[]} keys - Array of KeyTier values
   */
  setKeys(keys) {
    this._keys = [...keys];
    this._rebuildIcons();
  }

  /**
   * Add a key to the display with a pop-in effect.
   *
   * @param {string} tier - KeyTier value
   */
  addKey(tier) {
    this._keys.push(tier);
    this._rebuildIcons();

    // Pop-in effect on the newest icon
    const lastSprite = this._sprites[this._sprites.length - 1];
    if (lastSprite) {
      lastSprite.setScale(0);
      this.scene.tweens.add({
        targets: lastSprite,
        scaleX: 1.2,
        scaleY: 1.2,
        duration: 200,
        ease: 'Back.easeOut',
        onComplete: () => {
          this.scene.tweens.add({
            targets: lastSprite,
            scaleX: 1,
            scaleY: 1,
            duration: 100,
            ease: 'Sine.easeOut',
          });
        },
      });
    }
  }

  /**
   * Remove a key from the display with a magical disappearing effect.
   * Removes the first key matching the given tier.
   *
   * @param {string} tier - KeyTier value to remove
   */
  removeKey(tier) {
    const idx = this._keys.indexOf(tier);
    if (idx === -1) return;

    this._keys.splice(idx, 1);

    const sprite = this._sprites[idx];
    if (sprite) {
      this._sprites.splice(idx, 1);

      // Magical disappearing effect: sparkle particles + shrink + fade
      const color = TIER_COLOR[tier] || COLOR_METALLIC;

      // Sparkle particles
      const emitter = this.scene.add.particles(sprite.x, sprite.y, TEX_GLOW, {
        lifespan: { min: 200, max: 500 },
        speed: { min: 15, max: 40 },
        scale: { start: 0.3, end: 0 },
        alpha: { start: 0.9, end: 0 },
        frequency: -1, // manual emit
        quantity: 6,
        tint: color,
      });
      emitter.setDepth(220);
      emitter.explode(6);

      // Shrink + fade + spin
      this.scene.tweens.add({
        targets: sprite,
        scaleX: 0,
        scaleY: 0,
        alpha: 0,
        angle: 360,
        duration: 400,
        ease: 'Quad.easeIn',
        onComplete: () => {
          sprite.destroy();
          emitter.destroy();
          // Rebuild to realign remaining icons
          this._rebuildIcons();
        },
      });
    } else {
      this._rebuildIcons();
    }
  }

  /**
   * Play a collect effect (brief flash on all key icons).
   */
  playCollectEffect() {
    for (const sprite of this._sprites) {
      if (sprite && sprite.active) {
        this.scene.tweens.add({
          targets: sprite,
          scaleX: 1.3,
          scaleY: 1.3,
          duration: 100,
          yoyo: true,
          ease: 'Sine.easeInOut',
        });
      }
    }
  }

  /**
   * Destroy all display elements.
   */
  destroy() {
    for (const sprite of this._sprites) {
      if (sprite) sprite.destroy();
    }
    this._sprites = [];
    if (this._label) {
      this._label.destroy();
      this._label = null;
    }
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /**
   * Rebuild all key icon sprites from the current _keys array.
   * @private
   */
  _rebuildIcons() {
    // Destroy existing sprites
    for (const sprite of this._sprites) {
      if (sprite && sprite.active) sprite.destroy();
    }
    this._sprites = [];

    // Create new sprites
    const startX = this.x + 36; // After "KEYS:" label
    const spacing = 14;

    for (let i = 0; i < this._keys.length; i++) {
      const tier = this._keys[i];
      const texKey = KEY_TEXTURE_MAP[tier];
      if (!texKey) continue;

      const sprite = this.scene.add.sprite(
        startX + i * spacing,
        this.y,
        texKey,
      ).setOrigin(0.5).setDepth(210);

      this._sprites.push(sprite);
    }
  }
}
